import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import type { LucideIcon } from "lucide-react";
import * as Lucide from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
// Select imports removed — duration is now computed from start/end time fields
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Check, ChevronRight, Search, FileText,
  Calendar as CalendarIcon, Loader2, X, CheckCircle, Link2, Settings2, AlertCircle,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { format, addMinutes } from "date-fns";
import { ptBR } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { ilikeOrValue } from "@/lib/postgrestOr";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentOrgId, useCurrentUser } from "@/hooks/auth";
import { useOrganization } from "@/contexts/OrganizationContext";
import { useOrganizationUsers } from "@/hooks/admin/useOrganizationUsers";
import { PessoaAvatar } from "@/components/shared/PessoaAvatar";
import { useQueryClient } from "@tanstack/react-query";
import { useCreateActivity } from "@/hooks/activities/useActivities";
import { useOrganizationActivityTypes } from "@/hooks/settings/useOrganizationActivityTypes";
import { useOrganizationMeetingTypes } from "@/hooks/settings/useOrganizationMeetingTypes";
import { buildMergedActivityPickerOptions } from "@/lib/organizationActivityTypeOptions";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { OrganizationCustomFieldsForm } from "@/components/deals/OrganizationCustomFieldsForm";
import { CalendarSidebar } from "@/components/calendar/CalendarSidebar";
import { useCalendarEnabled } from "@/hooks/calendar/useCalendarEnabled";
import { isCalendarSyncable } from "@/lib/calendarSyncTypes";
import {
  MeetingParticipantsField,
  type MeetingInvitee,
  type MeetingParticipantLead,
} from "@/components/activities/MeetingParticipantsField";
import type { CalendarEventType } from "@/types/calendar-view";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ActivityTypeOption {
  value: string;
  label: string;
  icon: LucideIcon;
}

/** Dados do negócio para pré-preenchimento imediato (evita depender da lista assíncrona) */
interface PreselectedDealData {
  id: string;
  title: string;
  leads?: { id: string; name: string } | null;
  companies?: { id: string; name: string } | null;
}

interface ActivityCreateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  preselectedLeadId?: string;
  preselectedDealId?: string;
  preselectedDeal?: PreselectedDealData | null;
  preselectedType?: string;
  preselectedDateTime?: Date;
  preselectedCompanyId?: string;
  preselectedOwnerId?: string;
  /**
   * Contrato ao qual a atividade pertence (pós-venda). Excludente na prática com
   * `preselectedDealId`: reunião de entrega não é do funil de venda.
   */
  preselectedContractId?: string;
  /** Título já preenchido ao abrir (ex.: próximo passo vindo do resumo de uma reunião). */
  preselectedTitle?: string;
  /** Descrição gravada na atividade (o formulário não tem campo de descrição). */
  preselectedDescription?: string;
  /**
   * Convidados já preenchidos ao abrir. Existe porque a tela que abre o modal às vezes JÁ
   * SABE quem vai à reunião — a ficha do contrato tem os contatos do cliente cadastrados, e
   * redigitá-los só cria chance de erro (e-mail errado no convite = cliente sem convite).
   * Quem abre sem saber não passa nada e o campo começa vazio, como sempre; a lista segue
   * editável depois de preenchida.
   */
  preselectedInvitees?: MeetingInvitee[];
  isReschedule?: boolean;
}

interface SelectedRecord {
  id: string;
  name: string;
  company?: string;
  email?: string;
}

interface SelectedDeal {
  id: string;
  title: string;
}

interface LeadRecord {
  id: string;
  name: string;
  email: string | null;
  companies: { id: string; name: string } | null;
}

interface CompanyRecord {
  id: string;
  name: string;
}

interface DealRecord {
  id: string;
  title: string;
  lead_id: string | null;
  company_id: string | null;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

function resolveLucideIcon(name?: string | null): LucideIcon {
  if (!name) return FileText;
  const Icon = (Lucide as unknown as Record<string, LucideIcon | undefined>)[name];
  return Icon || FileText;
}

// Duration is now computed from start/end time fields (no fixed options)

const QUICK_TIMES = ["09:00", "10:00", "14:00", "15:00"];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function buildQuickDatePills() {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const nextBizDay = new Date(tomorrow);
  while (nextBizDay.getDay() === 0 || nextBizDay.getDay() === 6) {
    nextBizDay.setDate(nextBizDay.getDate() + 1);
  }

  const nextWeek = new Date(today);
  nextWeek.setDate(nextWeek.getDate() + 7);

  const pills: { label: string; value: Date }[] = [
    { label: "Hoje", value: today },
    { label: "Amanhã", value: tomorrow },
  ];

  if (nextBizDay.getTime() !== tomorrow.getTime()) {
    pills.push({
      label: format(nextBizDay, "EEEE", { locale: ptBR }).replace(/^\w/, (c) => c.toUpperCase()),
      value: nextBizDay,
    });
  }

  pills.push({ label: "Próxima semana", value: nextWeek });

  return pills;
}

/** Normalize string for accent-insensitive substring matching */
function normalize(str: string): string {
  return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
}

/** Check if a string matches a search term (accent/case insensitive substring) */
function matchesSearch(text: string, search: string): boolean {
  if (!search.trim()) return true;
  return normalize(text).includes(normalize(search));
}

function isSameDay(a: Date | undefined, b: Date): boolean {
  if (!a) return false;
  return a.toDateString() === b.toDateString();
}

// ---------------------------------------------------------------------------
// InlineSearchField — text input with live-filtered dropdown
// ---------------------------------------------------------------------------

interface SearchableListItem {
  id: string;
  label: string;
  /** Adorno à esquerda (foto da pessoa). Aparece na lista e no campo quando selecionado. */
  icon?: React.ReactNode;
}

interface InlineSearchFieldProps {
  value: SearchableListItem | null;
  items: SearchableListItem[];
  placeholder: string;
  emptyText: string;
  onSelect: (item: SearchableListItem) => void;
  onClear: () => void;
  loading?: boolean;
  onQueryChange?: (query: string) => void;
}

function InlineSearchField({ value, items, placeholder, emptyText, onSelect, onClear, loading, onQueryChange }: InlineSearchFieldProps) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [forceShowQuery, setForceShowQuery] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    setForceShowQuery(false);
  }, [value?.id]);

  // When onQueryChange is provided, items are server-filtered; skip local filter.
  const filtered = onQueryChange ? items : items.filter((item) => !query || matchesSearch(item.label, query));
  const displayValue = value && !forceShowQuery ? value.label : query;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newQuery = e.target.value;
    if (value) {
      setForceShowQuery(true);
      onClear();
    }
    setQuery(newQuery);
    setOpen(newQuery.length > 0);
    onQueryChange?.(newQuery);
  };

  const handleSelect = (item: SearchableListItem) => {
    setForceShowQuery(false);
    onSelect(item);
    setQuery("");
    setOpen(false);
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="relative flex items-center">
        {value?.icon && !forceShowQuery ? (
          <span className="absolute left-2.5 flex items-center pointer-events-none">{value.icon}</span>
        ) : (
          <Search className="absolute left-3 h-4 w-4 text-muted-foreground pointer-events-none" />
        )}
        <input
          value={displayValue}
          onChange={handleChange}
          placeholder={placeholder}
          className="h-9 w-full rounded-md border border-input bg-input-background pl-9 pr-8 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 placeholder:text-muted-foreground"
        />
        {value && (
          <button
            type="button"
            onClick={() => {
              setForceShowQuery(false);
              onClear();
              setQuery("");
            }}
            className="absolute right-2 opacity-50 hover:opacity-100"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      {open && (
        <div className="absolute z-50 w-full mt-1 rounded-md border bg-popover shadow-md">
          <div className="max-h-[200px] overflow-y-auto p-1">
            {loading ? (
              <p className="py-6 text-center text-sm text-muted-foreground">Carregando...</p>
            ) : filtered.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">{emptyText}</p>
            ) : (
              filtered.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleSelect(item)}
                  className="relative flex w-full cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-accent"
                >
                  {item.icon ? <span className="mr-2 flex shrink-0 items-center">{item.icon}</span> : null}
                  <span className="flex-1 text-left truncate">{item.label}</span>
                  {value?.id === item.id && <Check className="h-4 w-4 shrink-0 ml-2" />}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const ActivityCreateModal = ({
  open,
  onOpenChange,
  onSuccess,
  preselectedLeadId,
  preselectedDealId,
  preselectedDeal,
  preselectedType,
  preselectedDateTime,
  preselectedCompanyId,
  preselectedOwnerId,
  preselectedContractId,
  preselectedTitle,
  preselectedDescription,
  preselectedInvitees,
  isReschedule = false,
}: ActivityCreateModalProps) => {
  const { toast } = useToast();
  const { orgId } = useCurrentOrgId();
  const { currentOrg } = useOrganization();
  const { user: currentUser } = useCurrentUser();
  const { data: organizationUsers = [] } = useOrganizationUsers();
  const queryClient = useQueryClient();
  const createActivity = useCreateActivity();
  const { rows: orgActivityTypes } = useOrganizationActivityTypes();
  const { rows: meetingTypeRows } = useOrganizationMeetingTypes();
  const enabledMeetingTypes = useMemo(
    () => meetingTypeRows.filter((r) => r.enabled),
    [meetingTypeRows]
  );
  const { calendarEnabled } = useCalendarEnabled();
  const titleInputRef = useRef<HTMLInputElement>(null);
  const ownerSessionDefaultAppliedRef = useRef(false);
  const inviteesPrefillAppliedRef = useRef<string | null>(null);

  // ---- Form state ----
  const [activityType, setActivityType] = useState<string>("meeting");

  const typeOptions = useMemo<ActivityTypeOption[]>(() => {
    return buildMergedActivityPickerOptions(orgActivityTypes, resolveLucideIcon);
  }, [orgActivityTypes]);
  const [title, setTitle] = useState("");
  const [date, setDate] = useState<Date | undefined>(undefined);
  const [time, setTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [createAsCompleted, setCreateAsCompleted] = useState(false);
  const [selectedOwner, setSelectedOwner] = useState<SelectedRecord | null>(null);

  // Linked records
  const [selectedLead, setSelectedLead] = useState<SelectedRecord | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<{ id: string; name: string } | null>(null);
  const [selectedDeal, setSelectedDeal] = useState<SelectedDeal | null>(null);


  // Async data
  const [leads, setLeads] = useState<LeadRecord[]>([]);
  const [companies, setCompanies] = useState<CompanyRecord[]>([]);
  const [dataLoading, setDataLoading] = useState(false);

  // Deal server-side search
  const [dealSearch, setDealSearch] = useState("");
  const [dealResults, setDealResults] = useState<DealRecord[]>([]);
  const [dealSearchLoading, setDealSearchLoading] = useState(false);

  // Meeting type (tabulação obrigatória da reunião)
  const [meetingTypeId, setMeetingTypeId] = useState<string>("");

  // Meeting participants (convite / calendário)
  const [meetingInvitees, setMeetingInvitees] = useState<MeetingInvitee[]>([]);
  const [participantSearchQuery, setParticipantSearchQuery] = useState("");
  const [participantSearchResults, setParticipantSearchResults] = useState<MeetingParticipantLead[]>([]);
  const [participantSearchLoading, setParticipantSearchLoading] = useState(false);

  // UI state
  const [loading, setLoading] = useState(false);
  const [linkSectionOpen, setLinkSectionOpen] = useState(false);
  const [moreSectionOpen, setMoreSectionOpen] = useState(false);
  const [customData, setCustomData] = useState<Record<string, unknown>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  // ---- Reset / init on open/close ----
  useEffect(() => {
    if (!open) {
      setActivityType("meeting");
      setTitle("");
      setDate(undefined);
      setTime("");
      setEndTime("");
      setCreateAsCompleted(false);
      setSelectedLead(null);
      setSelectedCompany(null);
      setSelectedDeal(null);
      setSelectedOwner(null);
      setLinkSectionOpen(false);
      setMoreSectionOpen(false);
      setCustomData({});
      setErrors({});
      setMeetingTypeId("");
      setMeetingInvitees([]);
      setParticipantSearchQuery("");
      setParticipantSearchResults([]);
      setParticipantSearchLoading(false);
      setLeads([]);
      setCompanies([]);
      setDealSearch("");
      setDealResults([]);
      ownerSessionDefaultAppliedRef.current = false;
      inviteesPrefillAppliedRef.current = null;
    } else {
      const opts = buildMergedActivityPickerOptions(orgActivityTypes, resolveLucideIcon);
      if (preselectedType && opts.some((t) => t.value === preselectedType)) {
        setActivityType(preselectedType);
      }
      if (preselectedDateTime) {
        setDate(preselectedDateTime);
        setTime(format(preselectedDateTime, "HH:mm"));
        setEndTime(format(addMinutes(preselectedDateTime, 30), "HH:mm"));
      } else {
        setDate(new Date());
      }
      if (preselectedTitle) setTitle(preselectedTitle);
      // Autofocus title after mount
      requestAnimationFrame(() => {
        titleInputRef.current?.focus();
      });
    }
  }, [open, preselectedType, preselectedDateTime, preselectedTitle, orgActivityTypes]);

  // Responsável padrão só na abertura (ref); não reexecutar quando selectedOwner vira null ao buscar outro usuário.
  // Com `preselectedOwnerId` o padrão não entra: antes, quando o usuário logado carregava depois
  // da lista da org, este efeito sobrescrevia o dono pedido pela tela que abriu o modal.
  useEffect(() => {
    if (!open || !currentUser || ownerSessionDefaultAppliedRef.current || preselectedOwnerId) return;
    setSelectedOwner({
      id: currentUser.user_id,
      name: currentUser.full_name || currentUser.email,
    });
    ownerSessionDefaultAppliedRef.current = true;
  }, [open, currentUser, preselectedOwnerId]);

  // Convidados pré-preenchidos (ver `preselectedInvitees`).
  //
  // ⚠️ Guardado por CHAVE, não por referência de array: o chamador monta a lista e uma
  // referência nova a cada render reaplicaria o preenchimento por cima do que a pessoa
  // acabou de editar — tirar um convidado e vê-lo voltar sozinho. Mesmo desenho do
  // `ownerSessionDefaultAppliedRef` acima; a chave é o que permite reaplicar quando a
  // lista REALMENTE muda, em vez de travar no primeiro preenchimento.
  const inviteesPrefillKey = useMemo(
    () => (preselectedInvitees ?? []).map((i) => i.email).join(","),
    [preselectedInvitees],
  );
  useEffect(() => {
    if (!open || !inviteesPrefillKey) return;
    if (inviteesPrefillAppliedRef.current === inviteesPrefillKey) return;
    inviteesPrefillAppliedRef.current = inviteesPrefillKey;
    setMeetingInvitees(preselectedInvitees ?? []);
  }, [open, inviteesPrefillKey, preselectedInvitees]);

  // Pre-fill from preselectedDeal
  useEffect(() => {
    if (!open || !preselectedDeal?.id) return;
    setSelectedDeal({ id: preselectedDeal.id, title: preselectedDeal.title });
    if (preselectedDeal.leads?.id && preselectedDeal.leads?.name) {
      setSelectedLead({
        id: preselectedDeal.leads.id,
        name: preselectedDeal.leads.name,
        company: preselectedDeal.companies?.name ?? "Sem empresa",
      });
    }
    if (preselectedDeal.companies?.id && preselectedDeal.companies?.name) {
      setSelectedCompany({ id: preselectedDeal.companies.id, name: preselectedDeal.companies.name });
    }
    setLinkSectionOpen(true);
  }, [open, preselectedDeal?.id, preselectedDeal?.title, preselectedDeal?.leads?.id, preselectedDeal?.leads?.name, preselectedDeal?.companies?.id, preselectedDeal?.companies?.name]);

  // Pre-fill from preselectedLeadId.
  // We fetch by id directly because the `leads` picker list is paginated by
  // Supabase (default limit 1000) — for orgs with many leads, the active lead
  // may not be in that list, so a `leads.find(...)` would silently miss.
  useEffect(() => {
    if (!open || !preselectedLeadId) return;
    let cancelled = false;
    (async () => {
      const fromList = leads.find((l) => l.id === preselectedLeadId);
      if (fromList) {
        if (cancelled) return;
        setSelectedLead({
          id: fromList.id,
          name: fromList.name,
          company: fromList.companies?.name || "Sem empresa",
          email: fromList.email ?? undefined,
        });
        if (fromList.companies?.id && fromList.companies?.name) {
          setSelectedCompany({ id: fromList.companies.id, name: fromList.companies.name });
        }
        setLinkSectionOpen(true);
        return;
      }
      const { data: lead, error } = await supabase
        .from("leads")
        .select("id, name, email, companies(id, name)")
        .eq("id", preselectedLeadId)
        .maybeSingle();
      if (cancelled || error || !lead) return;
      const companies = (lead as any).companies as { id: string; name: string } | null;
      setSelectedLead({
        id: lead.id,
        name: lead.name,
        company: companies?.name || "Sem empresa",
        email: lead.email ?? undefined,
      });
      if (companies?.id && companies?.name) {
        setSelectedCompany({ id: companies.id, name: companies.name });
      }
      setLinkSectionOpen(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, preselectedLeadId, leads]);

  // Pre-fill from preselectedDealId — fetch by ID directly
  useEffect(() => {
    if (!open || !preselectedDealId) return;
    supabase
      .from("deals")
      .select("id, title, lead_id, company_id, leads!deals_lead_id_fkey(id, name, email, companies(id, name)), companies(id, name)")
      .eq("id", preselectedDealId)
      .single()
      .then(({ data: deal }) => {
        if (!deal) return;
        setSelectedDeal({ id: deal.id, title: deal.title });
        const joinedLead = (deal as any).leads as { id: string; name: string; email: string | null; companies: { id: string; name: string } | null } | null;
        const joinedCompany = (deal as any).companies as { id: string; name: string } | null;
        if (joinedLead) {
          setSelectedLead({
            id: joinedLead.id,
            name: joinedLead.name,
            company: joinedLead.companies?.name || "Sem empresa",
            email: joinedLead.email ?? undefined,
          });
        }
        if (joinedCompany) {
          setSelectedCompany({ id: joinedCompany.id, name: joinedCompany.name });
        }
        setLinkSectionOpen(true);
      });
  }, [open, preselectedDealId]);  

  useEffect(() => {
    if (!open || !preselectedCompanyId || !orgId) return;
    supabase
      .from("companies")
      .select("id, name")
      .eq("id", preselectedCompanyId)
      .eq("org_id", orgId)
      .maybeSingle()
      .then(({ data }) => {
        if (data) {
          setSelectedCompany({ id: data.id, name: data.name });
          setLinkSectionOpen(true);
        }
      });
  }, [open, preselectedCompanyId, orgId]);

  useEffect(() => {
    if (!open || !preselectedOwnerId || !organizationUsers.length) return;
    const u = organizationUsers.find((x) => x.user_id === preselectedOwnerId);
    if (u) {
      setSelectedOwner({ id: u.user_id, name: u.full_name || u.email || "" });
    }
  }, [open, preselectedOwnerId, organizationUsers]);

  // Auto-add lead email to meeting participants when lead is selected
  useEffect(() => {
    if (!selectedLead?.id || activityType !== "meeting") return;
    const email = selectedLead.email?.trim();
    if (!email) return;
    const em = email.toLowerCase();
    setMeetingInvitees((prev) => {
      if (prev.some((p) => p.email.toLowerCase() === em)) return prev;
      return [{ email, name: selectedLead.name }, ...prev];
    });
  }, [selectedLead?.id, selectedLead?.email, activityType]);

  // Fetch leads, companies, deals
  useEffect(() => {
    if (!orgId || !open) return;

    const fetchData = async () => {
      setDataLoading(true);
      try {
        const [leadsRes, companiesRes] = await Promise.all([
          supabase.from("leads").select("id, name, email, companies(id, name)").eq("org_id", orgId),
          supabase.from("companies").select("id, name").eq("org_id", orgId),
        ]);
        if (leadsRes.error) throw leadsRes.error;
        if (companiesRes.error) throw companiesRes.error;
        setLeads((leadsRes.data as LeadRecord[]) || []);
        setCompanies((companiesRes.data as CompanyRecord[]) || []);
      } catch (err) {
        console.error("[ActivityCreateModal] fetchData error:", err);
        toast({ title: "Erro", description: "Não foi possível carregar os dados.", variant: "destructive" });
      } finally {
        setDataLoading(false);
      }
    };

    fetchData();
  }, [orgId, open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Server-side deal search with debounce
  useEffect(() => {
    if (!orgId || !open || !dealSearch.trim()) {
      setDealResults([]);
      return;
    }
    setDealSearchLoading(true);
    const timer = setTimeout(async () => {
      try {
        const { data, error } = await supabase
          .from("deals")
          .select("id, title, lead_id, company_id")
          .eq("org_id", orgId)
          .ilike("title", `%${dealSearch}%`)
          .limit(20);
        if (error) throw error;
        setDealResults((data as DealRecord[]) || []);
      } catch (err) {
        console.error("[ActivityCreateModal] dealSearch error:", err);
      } finally {
        setDealSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [dealSearch, orgId, open]);  

  // Busca de participantes (leads) no servidor — nome ou e-mail, todos os registros da org (limite 50)
  useEffect(() => {
    if (!orgId || !open) {
      setParticipantSearchResults([]);
      return;
    }
    const raw = participantSearchQuery.trim().replace(/,/g, " ").trim();
    if (!raw) {
      setParticipantSearchResults([]);
      setParticipantSearchLoading(false);
      return;
    }
    setParticipantSearchLoading(true);
    const timer = setTimeout(async () => {
      try {
        const safe = ilikeOrValue(raw);
        const { data, error } = await supabase
          .from("leads")
          .select("id, name, email, companies(id, name), deals!deals_lead_id_fkey(id, title, updated_at)")
          .eq("org_id", orgId)
          .or(`name.ilike.${safe},email.ilike.${safe}`)
          .limit(50);
        if (error) throw error;
        setParticipantSearchResults((data as MeetingParticipantLead[]) || []);
      } catch (err) {
        console.error("[ActivityCreateModal] participantSearch error:", err);
        setParticipantSearchResults([]);
      } finally {
        setParticipantSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [participantSearchQuery, orgId, open]);

  // ---- Submission ----
  const handleSave = useCallback(async () => {
    // Clear previous errors
    setErrors({});
    const newErrors: Record<string, string> = {};

    if (!activityType) newErrors.type = "Selecione o tipo de atividade.";
    if (!date) newErrors.date = "Selecione a data da atividade.";
    if (!orgId) newErrors.org = "Não foi possível identificar a organização.";
    if (activityType === "meeting" && enabledMeetingTypes.length > 0 && !meetingTypeId) {
      newErrors.meetingType = "Selecione o tipo de reunião.";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);

    try {
      const activityDate = new Date(date!);
      if (time) {
        const [hours, minutes] = time.split(":");
        activityDate.setHours(parseInt(hours), parseInt(minutes));
      } else {
        activityDate.setHours(0, 0, 0, 0);
      }

      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      const typeLabel = typeOptions.find((t) => t.value === activityType)?.label ?? activityType;
      const orgName = currentOrg?.name || "";
      const companyOrLeadName = selectedCompany?.name || selectedLead?.name || "";
      const generatedTitle = title.trim()
        || (["meeting", "call"].includes(activityType) && orgName && companyOrLeadName
          ? `${orgName} + ${companyOrLeadName}`
          : `${typeLabel} · ${selectedLead?.name ?? "Sem pessoa"}`);

      const activityData: Record<string, unknown> = {
        type: activityType,
        activity_type_key: activityType,
        title: generatedTitle,
        description: preselectedDescription?.trim() || null,
        date: activityDate.toISOString(),
        status: createAsCompleted ? "done" : "pending",
        created_by: user.id,
        lead_id: selectedLead?.id || null,
        company_id: selectedCompany?.id || null,
      };

      // Compute duration from start/end times
      if (time && endTime) {
        const [sh, sm] = time.split(":").map(Number);
        const [eh, em] = endTime.split(":").map(Number);
        const computedDuration = (eh * 60 + em) - (sh * 60 + sm);
        if (computedDuration > 0) activityData.duration_minutes = computedDuration;
      }
      if (selectedDeal?.id) activityData.deal_id = selectedDeal.id;
      if (preselectedContractId) activityData.contract_id = preselectedContractId;
      if (selectedOwner?.id) activityData.owner_id = selectedOwner.id;
      if (activityType === "meeting" && meetingTypeId) activityData.meeting_type_id = meetingTypeId;
      const customMerged: Record<string, unknown> = { ...customData };
      if (activityType === "meeting" && meetingInvitees.length > 0) {
        customMerged.meeting_invitees = meetingInvitees;
      }
      if (Object.keys(customMerged).length > 0) activityData.custom_data = customMerged;

      const newActivity = await createActivity.mutateAsync(activityData);

      console.log("[ActivityCreateModal] Activity created:", newActivity?.id, "type:", activityType);

      // Append activity_id to description
      if (newActivity?.id) {
        const currentDesc = activityData.description || "";
        const descWithId = [currentDesc, `activity_id: ${newActivity.id}`].filter(Boolean).join("\n\n");
        await supabase.from("activities").update({ description: descWithId }).eq("id", newActivity.id);
      }

      // A sincronização com o Google Calendar na CRIAÇÃO é feita pelo trigger
      // de banco `activity_calendar_sync_trigger` (cobre todas as origens:
      // modal, cadência, automação, VoIP). Não disparamos push aqui para evitar
      // evento duplicado. Convidados de reunião ficam persistidos em
      // custom_data.meeting_invitees e são lidos pela edge function.

      onOpenChange(false);

      // Invalidate related queries
      if (selectedLead?.id) queryClient.invalidateQueries({ queryKey: ["lead-activities", selectedLead.id] });
      if (selectedCompany?.id) queryClient.invalidateQueries({ queryKey: ["company-activities", selectedCompany.id] });
      if (selectedDeal?.id) queryClient.invalidateQueries({ queryKey: ["deal-activities", selectedDeal.id] });

      onSuccess?.();
    } catch {
      // Error toast is handled by useCreateActivity hook
    } finally {
      setLoading(false);
    }
  }, [activityType, date, time, endTime, title, createAsCompleted, calendarEnabled, selectedLead, selectedCompany, selectedDeal, selectedOwner, customData, meetingInvitees, orgId, preselectedContractId, preselectedDescription, onOpenChange, onSuccess, createActivity, queryClient]);

  // Keyboard shortcut: Cmd/Ctrl + Enter to save
  const handleSaveRef = useRef(handleSave);
  handleSaveRef.current = handleSave;
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
        e.preventDefault();
        handleSaveRef.current();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open]);

  // ---- Quick date pills ----
  const quickDatePills = buildQuickDatePills();

  // ---- Auto-select lead helpers ----
  const handleLeadSelect = (lead: LeadRecord) => {
    setSelectedLead({
      id: lead.id,
      name: lead.name,
      company: lead.companies?.name || "Sem empresa",
      email: lead.email ?? undefined,
    });
    if (lead.companies?.id && lead.companies?.name) {
      setSelectedCompany({ id: lead.companies.id, name: lead.companies.name });
    }
  };

  const handleDealSelect = (deal: DealRecord) => {
    setSelectedDeal({ id: deal.id, title: deal.title });
    setDealSearch("");
    setDealResults([]);
    const lead = deal.lead_id ? leads.find((l) => l.id === deal.lead_id) : null;
    const company = deal.company_id ? companies.find((c) => c.id === deal.company_id) : null;
    if (lead) {
      setSelectedLead({
        id: lead.id,
        name: lead.name,
        company: lead.companies?.name || "Sem empresa",
      });
    }
    if (company) {
      setSelectedCompany({ id: company.id, name: company.name });
    }
  };

  // Count linked records for badge
  const linkedCount = [selectedLead, selectedCompany, selectedDeal].filter(Boolean).length;

  // ---- Render ----
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        hideClose
        className={cn(
          "flex flex-col p-0 gap-0 w-full max-h-[85vh]",
          // Wider on desktop to fit calendar sidebar
          "max-w-[600px] lg:max-w-[920px]",
          "sm:rounded-lg",
          // Mobile: full screen sheet
          "max-sm:max-w-full max-sm:h-full max-sm:max-h-full max-sm:rounded-none"
        )}
      >
        {/* ================================================================ */}
        {/* HEADER — sticky top                                             */}
        {/* ================================================================ */}
        <div className="flex items-center justify-between px-5 py-4 border-b bg-background shrink-0">
          <DialogTitle className="text-lg font-semibold">
            {isReschedule ? "Reagendar atividade" : "Nova atividade"}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Formulário para criar uma nova atividade
          </DialogDescription>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="rounded-sm p-1 opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            aria-label="Fechar"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* ================================================================ */}
        {/* BODY — scrollable (with optional calendar sidebar)              */}
        {/* ================================================================ */}
        <div className="flex-1 flex min-h-0 overflow-hidden">
        {/* Form column */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {/* ---- Type Selector ---- */}
          <div className="space-y-2" role="radiogroup" aria-label="Tipo de atividade">
            <div className="flex flex-wrap gap-2">
              {typeOptions.map((type) => {
                const Icon = type.icon;
                const isSelected = activityType === type.value;
                return (
                  <button
                    key={type.value}
                    type="button"
                    role="radio"
                    aria-checked={isSelected}
                    onClick={() => {
                      setActivityType(type.value);
                      setErrors((prev) => ({ ...prev, type: "" }));
                    }}
                    className={cn(
                      "flex flex-col items-center gap-1.5 p-3 rounded-lg border-2 transition-all text-center min-h-[44px]",
                      isSelected
                        ? "border-primary bg-primary-50 ring-2 ring-primary/30"
                        : "border-border hover:border-primary/30 bg-card"
                    )}
                  >
                    <div className="relative">
                      <Icon className={cn("h-5 w-5", isSelected ? "text-primary" : "text-muted-foreground")} />
                      {isSelected && (
                        <CheckCircle className="absolute -top-1 -right-2 h-3 w-3 text-primary" />
                      )}
                    </div>
                    <span className={cn("text-xs font-medium", isSelected ? "text-primary" : "text-muted-foreground")}>
                      {type.label}
                    </span>
                  </button>
                );
              })}
            </div>
            {errors.type && (
              <p className="text-xs text-destructive flex items-center gap-1">
                <AlertCircle className="h-3 w-3" /> {errors.type}
              </p>
            )}
          </div>

          {/* ---- Title ---- */}
          <div className="space-y-1.5">
            <Label htmlFor="activity-title">Título</Label>
            <Input
              ref={titleInputRef}
              id="activity-title"
              placeholder="Follow-up call com cliente"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="text-base h-11"
              autoComplete="off"
            />
            {!title.trim() && (
              <p className="text-xs text-muted-foreground">
                Se vazio, será gerado: &ldquo;{
                  ["meeting", "call"].includes(activityType) && currentOrg?.name && (selectedCompany?.name || selectedLead?.name)
                    ? `${currentOrg.name} + ${selectedCompany?.name || selectedLead?.name}`
                    : `${typeOptions.find((t) => t.value === activityType)?.label ?? ""} · ${selectedLead?.name ?? "nome da pessoa"}`
                }&rdquo;
              </p>
            )}
          </div>

          {/* ---- Date + Start Time + End Time ---- */}
          <div className="grid grid-cols-[1.4fr_1fr_1fr] gap-3">
            {/* Date */}
            <div className="space-y-1.5">
              <Label>Data</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className={cn(
                      "w-full justify-start text-left font-normal h-9",
                      !date && "text-muted-foreground",
                      errors.date && "border-destructive"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {date ? format(date, "dd/MM/yyyy") : "Selecione"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={(d) => {
                      setDate(d);
                      setErrors((prev) => ({ ...prev, date: "" }));
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              {errors.date && (
                <p className="text-xs text-destructive flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" /> {errors.date}
                </p>
              )}
            </div>

            {/* Start Time */}
            <div className="space-y-1.5">
              <Label htmlFor="activity-time">Início</Label>
              <Input
                id="activity-time"
                type="time"
                value={time}
                onChange={(e) => {
                  const newStart = e.target.value;
                  setTime(newStart);
                  // Auto-set end time +30min if not set or if end <= start
                  if (newStart && (!endTime || newStart >= endTime)) {
                    const [h, m] = newStart.split(":").map(Number);
                    const end = addMinutes(new Date(2000, 0, 1, h, m), 30);
                    setEndTime(format(end, "HH:mm"));
                  }
                }}
                placeholder="--:--"
                className="h-9"
              />
            </div>

            {/* End Time */}
            <div className="space-y-1.5">
              <Label htmlFor="activity-end-time">Fim</Label>
              <Input
                id="activity-end-time"
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                placeholder="--:--"
                className="h-9"
              />
            </div>
          </div>

          {/* ---- Tipo de reunião (tabulação obrigatória) ---- */}
          {activityType === "meeting" && enabledMeetingTypes.length > 0 && (
            <div className="space-y-1.5">
              <Label htmlFor="meeting-type">
                Tipo de reunião <span className="text-destructive">*</span>
              </Label>
              <Select
                value={meetingTypeId}
                onValueChange={(v) => {
                  setMeetingTypeId(v);
                  setErrors((prev) => {
                    if (!prev.meetingType) return prev;
                    const next = { ...prev };
                    delete next.meetingType;
                    return next;
                  });
                }}
              >
                <SelectTrigger id="meeting-type" className={cn("h-9", errors.meetingType && "border-destructive")}>
                  <SelectValue placeholder="Selecione a finalidade da reunião" />
                </SelectTrigger>
                <SelectContent>
                  {enabledMeetingTypes.map((mt) => (
                    <SelectItem key={mt.id} value={mt.id}>
                      {mt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.meetingType && (
                <p className="text-xs text-destructive flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" /> {errors.meetingType}
                </p>
              )}
            </div>
          )}

          {/* ---- Participantes (reunião) — acima do responsável ---- */}
          {activityType === "meeting" && (
            <MeetingParticipantsField
              invitees={meetingInvitees}
              onChange={setMeetingInvitees}
              onSearchQueryChange={setParticipantSearchQuery}
              searchResults={participantSearchResults}
              searchLoading={participantSearchLoading}
            />
          )}

          {/* ---- Responsável ---- */}
          <div className="space-y-1.5">
            <Label>Responsável</Label>
            <InlineSearchField
              placeholder="Buscar responsável..."
              emptyText="Nenhum usuário encontrado."
              items={organizationUsers.map((u) => ({
                id: u.user_id,
                label: u.full_name || u.email || "",
                icon: <PessoaAvatar nome={u.full_name || u.email} avatarUrl={u.avatar_url} />,
              }))}
              value={selectedOwner ? {
                id: selectedOwner.id,
                label: selectedOwner.name,
                icon: <PessoaAvatar nome={selectedOwner.name} avatarUrl={organizationUsers.find((u) => u.user_id === selectedOwner.id)?.avatar_url} />,
              } : null}
              onSelect={(item) => setSelectedOwner({ id: item.id, name: item.label })}
              onClear={() => setSelectedOwner(null)}
            />
          </div>

          {/* ---- Vincular a registro (collapsible) ---- */}
          <Collapsible open={linkSectionOpen} onOpenChange={setLinkSectionOpen}>
            <CollapsibleTrigger asChild>
              <button
                type="button"
                className="flex items-center gap-2 w-full text-sm text-muted-foreground hover:text-foreground transition-colors py-2"
                aria-expanded={linkSectionOpen}
              >
                <Link2 className="h-4 w-4" />
                <span className="font-medium">Vincular a registro</span>
                {linkedCount > 0 && (
                  <span className="bg-primary/10 text-primary text-xs px-1.5 py-0.5 rounded-full font-medium">
                    {linkedCount}
                  </span>
                )}
                <ChevronRight
                  className={cn(
                    "h-4 w-4 ml-auto transition-transform",
                    linkSectionOpen && "rotate-90"
                  )}
                />
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-3 pt-2 data-[state=open]:animate-accordion-down data-[state=closed]:animate-accordion-up overflow-hidden">
              {/* Negócio */}
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Negócio</Label>
                <InlineSearchField
                  placeholder="Buscar negócio..."
                  emptyText="Nenhum negócio encontrado."
                  loading={dealSearchLoading}
                  items={dealResults.map((d) => ({ id: d.id, label: d.title }))}
                  value={selectedDeal ? { id: selectedDeal.id, label: selectedDeal.title } : null}
                  onQueryChange={setDealSearch}
                  onSelect={(item) => {
                    const deal = dealResults.find((d) => d.id === item.id);
                    if (deal) handleDealSelect(deal);
                  }}
                  onClear={() => { setSelectedDeal(null); setDealSearch(""); setDealResults([]); }}
                />
              </div>

              {/* Empresa */}
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Empresa</Label>
                <InlineSearchField
                  placeholder="Buscar empresa..."
                  emptyText="Nenhuma empresa encontrada."
                  loading={dataLoading}
                  items={companies.map((c) => ({ id: c.id, label: c.name }))}
                  value={selectedCompany ? { id: selectedCompany.id, label: selectedCompany.name } : null}
                  onSelect={(item) => setSelectedCompany({ id: item.id, name: item.label })}
                  onClear={() => setSelectedCompany(null)}
                />
              </div>

              {/* Lead */}
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Lead</Label>
                <InlineSearchField
                  placeholder="Buscar lead..."
                  emptyText="Nenhum lead encontrado."
                  loading={dataLoading}
                  items={leads.map((l) => ({ id: l.id, label: l.name }))}
                  value={selectedLead ? { id: selectedLead.id, label: selectedLead.name } : null}
                  onSelect={(item) => {
                    const lead = leads.find((l) => l.id === item.id);
                    if (lead) handleLeadSelect(lead);
                  }}
                  onClear={() => setSelectedLead(null)}
                />
              </div>
            </CollapsibleContent>
          </Collapsible>

          {/* ---- Mais opções (collapsible) ---- */}
          <Collapsible open={moreSectionOpen} onOpenChange={setMoreSectionOpen}>
            <CollapsibleTrigger asChild>
              <button
                type="button"
                className="flex items-center gap-2 w-full text-sm text-muted-foreground hover:text-foreground transition-colors py-2"
                aria-expanded={moreSectionOpen}
              >
                <Settings2 className="h-4 w-4" />
                <span className="font-medium">Mais opções</span>
                <span className="text-xs text-muted-foreground/70">Campos personalizados</span>
                <ChevronRight
                  className={cn(
                    "h-4 w-4 ml-auto transition-transform",
                    moreSectionOpen && "rotate-90"
                  )}
                />
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-3 pt-2 data-[state=open]:animate-accordion-down data-[state=closed]:animate-accordion-up overflow-hidden">
              {/* Custom fields */}
              <OrganizationCustomFieldsForm
                customData={customData as Record<string, string>}
                pageType="activities"
                onCustomDataChange={(data) => setCustomData(data)}
              />
            </CollapsibleContent>
          </Collapsible>
        </div>

        {/* Calendar sidebar — all types, hidden on mobile */}
        <div className="hidden lg:flex w-[300px] flex-shrink-0 min-h-0">
          <CalendarSidebar
            userId={selectedOwner?.id ?? currentUser?.user_id}
            selectedDate={date ?? new Date()}
            selectedTime={
              date && time
                ? (() => {
                    const [h, m] = time.split(":").map(Number);
                    const d = new Date(date);
                    d.setHours(h, m, 0, 0);
                    return d;
                  })()
                : undefined
            }
            preview={
              date && time
                ? (() => {
                    const [h, m] = time.split(":").map(Number);
                    const startDate = new Date(date);
                    startDate.setHours(h, m, 0, 0);
                    // Compute duration from start/end
                    let dur = 30;
                    if (endTime) {
                      const [eh, em] = endTime.split(":").map(Number);
                      dur = Math.max(15, (eh * 60 + em) - (h * 60 + m));
                    }
                    const previewType: CalendarEventType = (() => {
                      if (activityType === "whatsapp") return "call";
                      const allowed: CalendarEventType[] = ["call", "email", "meeting", "task", "note", "proposal"];
                      if (allowed.includes(activityType as CalendarEventType)) {
                        return activityType as CalendarEventType;
                      }
                      return "task";
                    })();
                    return {
                      type: previewType,
                      startTime: startDate,
                      durationMinutes: dur,
                      title: title || `${typeOptions.find((t) => t.value === activityType)?.label ?? activityType}`,
                    };
                  })()
                : undefined
            }
            onSlotSelect={(slotTime) => {
              setDate(slotTime);
              setTime(format(slotTime, "HH:mm"));
              setEndTime(format(addMinutes(slotTime, 30), "HH:mm"));
            }}
            onDateChange={(newDate) => {
              setDate(newDate);
            }}
            onPreviewResize={(newDuration) => {
              // Update end time based on new duration
              if (time) {
                const [h, m] = time.split(":").map(Number);
                const newEnd = addMinutes(new Date(2000, 0, 1, h, m), newDuration);
                setEndTime(format(newEnd, "HH:mm"));
              }
            }}
            onPreviewMove={(newStartTime) => {
              // Keep duration, shift both start and end
              const oldDur = (() => {
                if (time && endTime) {
                  const [sh, sm] = time.split(":").map(Number);
                  const [eh, em] = endTime.split(":").map(Number);
                  return Math.max(15, (eh * 60 + em) - (sh * 60 + sm));
                }
                return 30;
              })();
              setDate(newStartTime);
              setTime(format(newStartTime, "HH:mm"));
              setEndTime(format(addMinutes(newStartTime, oldDur), "HH:mm"));
            }}
            className="w-full"
          />
        </div>
        </div>

        {/* ================================================================ */}
        {/* FOOTER — sticky bottom                                          */}
        {/* ================================================================ */}
        <div className="flex items-center justify-between px-5 py-3 border-t bg-background shrink-0 gap-3">
          {/* Left: checkboxes */}
          <div className="flex items-center gap-4 shrink-0">
            <div className="flex items-center gap-2">
              <Checkbox
                id="createAsCompleted"
                checked={createAsCompleted}
                onCheckedChange={(checked) => setCreateAsCompleted(!!checked)}
              />
              <Label
                htmlFor="createAsCompleted"
                className="text-xs font-medium cursor-pointer select-none whitespace-nowrap"
              >
                Criar como concluída
              </Label>
            </div>
            {calendarEnabled && isCalendarSyncable(activityType) && Boolean(time) && (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground whitespace-nowrap">
                <CalendarIcon className="h-3.5 w-3.5" />
                Será adicionada ao Google Calendar
              </div>
            )}
          </div>

          {/* Right: actions */}
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-muted-foreground hidden sm:inline mr-1">⌘↵</span>
            <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)} disabled={loading}>
              Cancelar
            </Button>
            <Button
              variant={createAsCompleted ? "success" : "default"}
              size="sm"
              onClick={handleSave}
              disabled={loading || !date}
            >
              {loading && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
              {createAsCompleted ? "Criar como concluída" : "Criar atividade"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
